import React, { useState, useEffect } from 'react';
import './App.css';
import { getApiUrl } from './apiConfig';

export default function Procurement({ onBackClick }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'closed'
    const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'pdf', 'image'
    const [selectedImageModal, setSelectedImageModal] = useState(null);

    // Open PDF safely (supports Base64 data URIs as well as standard URLs)
    const openPdf = (fileData, fileName = 'Document.pdf') => {
        if (!fileData) return;
        try {
            if (fileData.startsWith('data:application/pdf')) {
                const base64Parts = fileData.split(',');
                const byteCharacters = atob(base64Parts[1]);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const blobUrl = URL.createObjectURL(blob);
                window.open(blobUrl, '_blank');
            } else {
                window.open(fileData, '_blank');
            }
        } catch (err) {
            console.error('Error opening PDF:', err);
            window.open(fileData, '_blank');
        }
    };

    // Download PDF or Image
    const downloadFile = (fileData, fileName, fileType) => {
        if (!fileData) return;
        const link = document.createElement('a');
        link.href = fileData;
        link.download = fileName || (fileType === 'pdf' ? 'procurement_notice.pdf' : 'procurement_image.jpg');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    useEffect(() => {
        const fetchProcurementData = async () => {
            setLoading(true);
            try {
                const res = await fetch(getApiUrl(`api_procurement.php?t=${Date.now()}`));
                if (res.ok) {
                    const data = await res.json();
                    if (Array.isArray(data) && data.length > 0) {
                        setItems(data);
                        try { localStorage.setItem('site_procurement', JSON.stringify(data)); } catch (e) {}
                        return;
                    }
                }
                throw new Error('API response empty or invalid');
            } catch (err) {
                console.warn('Procurement API call failed, trying fallback...', err);
                try {
                    const staticRes = await fetch(`/procurement_data.json?t=${Date.now()}`);
                    if (staticRes.ok) {
                        const staticData = await staticRes.json();
                        if (Array.isArray(staticData)) {
                            setItems(staticData);
                            return;
                        }
                    }
                } catch (staticErr) {
                    console.warn('Static data fetch failed:', staticErr);
                }

                // LocalStorage fallback
                const local = localStorage.getItem('site_procurement');
                if (local) {
                    try {
                        setItems(JSON.parse(local));
                    } catch (parseErr) {
                        setItems([]);
                    }
                }
            } finally {
                setLoading(false);
            }
        };

        fetchProcurementData();
    }, []);

    // Filter items
    const filteredItems = items.filter(item => {
        const matchesSearch = 
            item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.date?.includes(searchTerm);

        const matchesStatus = statusFilter === 'all' || (item.status || 'active') === statusFilter;
        const matchesType = typeFilter === 'all' || item.fileType === typeFilter;

        return matchesSearch && matchesStatus && matchesType;
    });

    return (
        <main className="page-content procurement-page" style={{ padding: '28px 16px', paddingBottom: '120px', maxWidth: '1240px', margin: '0 auto', position: 'relative' }}>
            <button
                className="back-button"
                onClick={() => onBackClick && onBackClick('home')}
                style={{
                    backgroundColor: '#0b6b2f',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                }}
            >
                ← ආපසු
            </button>

            <h1 style={{ color: '#0b6b2f', fontSize: 28, marginBottom: 12, textAlign: 'justify' }}>ප්‍රසම්පාදන</h1>
            <hr className="footer-sep" />

          

            {/* Filter and Search Bar */}
            <div style={{
                background: '#ffffff',
                padding: '20px 24px',
                borderRadius: '12px',
                marginBottom: '30px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                border: '1px solid #e2e8f0',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '16px',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                {/* Search Input */}
                <div style={{ flex: '1', minWidth: '260px' }}>
                    <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '16px' }}>🔍</span>
                        <input
                            type="text"
                            placeholder="මාතෘකාව හෝ දිනය අනුව සොයන්න..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '12px 14px 12px 42px',
                                borderRadius: '8px',
                                border: '1.5px solid #cbd5e1',
                                fontSize: '14.5px',
                                outline: 'none',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>
                </div>

                {/* Filter Controls */}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Media Type filter */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>මාධ්‍ය:</span>
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            style={{
                                padding: '10px 14px',
                                borderRadius: '8px',
                                border: '1px solid #cbd5e1',
                                fontSize: '13.5px',
                                background: 'white',
                                outline: 'none'
                            }}
                        >
                            <option value="all">සියල්ල (All Media)</option>
                            <option value="pdf">📄 PDF ලේඛන</option>
                            <option value="image">🖼️ ඡායාරූප</option>
                        </select>
                    </div>

                    {/* Status filter */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>තත්ත්වය:</span>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{
                                padding: '10px 14px',
                                borderRadius: '8px',
                                border: '1px solid #cbd5e1',
                                fontSize: '13.5px',
                                background: 'white',
                                outline: 'none'
                            }}
                        >
                            <option value="all">සියල්ල (All Status)</option>
                            <option value="active">🟢 සක්‍රියයි (Open)</option>
                            <option value="closed">🔴 අවසන් (Closed)</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Procurement Cards Grid */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '70px 20px', color: '#64748b' }}>
                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔄</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>ප්‍රසම්පාදන දැන්වීම් පූරණය වෙමින් පවතී...</div>
                </div>
            ) : filteredItems.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    background: '#ffffff',
                    borderRadius: '14px',
                    border: '1px dashed #cbd5e1',
                    color: '#64748b'
                }}>
                    <div style={{ fontSize: '42px', marginBottom: '12px' }}>📭</div>
                    <h3 style={{ margin: '0 0 8px 0', color: '#334155' }}>දැනට ප්‍රසම්පාදන දැන්වීම් හමු නොවීය</h3>
                    <p style={{ margin: 0, fontSize: '14px' }}>
                        {searchTerm ? 'ඔබ සොයන නිර්ණායකයන්ට ගැලපෙන දැන්වීම් කිසිවක් හමු නොවීය.' : 'නව ටෙන්ඩර් නිවේදන නිකුත් වූ වහාම මෙහි පළ කරනු ලැබේ.'}
                    </p>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '24px' }}>
                    {filteredItems.map(item => {
                        const isPdf = item.fileType === 'pdf' || (item.fileData && item.fileData.includes('pdf'));
                        const isActive = (item.status || 'active') === 'active';

                        return (
                            <div
                                key={item.id}
                                style={{
                                    background: '#ffffff',
                                    borderRadius: '14px',
                                    border: '1px solid #e2e8f0',
                                    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.05)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden',
                                    transition: 'transform 0.2s, box-shadow 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 10px 24px rgba(0, 0, 0, 0.08)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(0, 0, 0, 0.05)';
                                }}
                            >
                                {/* Media Banner (Image thumbnail or PDF Header) */}
                                {isPdf ? (
                                    <div style={{
                                        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                                        padding: '24px 20px',
                                        borderBottom: '1px solid #e2e8f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '14px'
                                    }}>
                                        <div style={{
                                            width: '52px',
                                            height: '52px',
                                            background: '#fee2e2',
                                            color: '#ef4444',
                                            borderRadius: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '26px',
                                            boxShadow: '0 2px 8px rgba(239, 68, 68, 0.2)'
                                        }}>
                                            📄
                                        </div>
                                        <div>
                                            <span style={{
                                                fontSize: '11px',
                                                fontWeight: '700',
                                                color: '#b91c1c',
                                                background: '#fee2e2',
                                                padding: '2px 8px',
                                                borderRadius: '4px',
                                                letterSpacing: '0.4px'
                                            }}>
                                                PDF DOCUMENT
                                            </span>
                                            <div style={{ fontSize: '13px', color: '#475569', marginTop: '4px', fontWeight: '500' }}>
                                                {item.fileName || 'ලේඛනය බාගත කළ හැක'}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        style={{
                                            height: '190px',
                                            background: '#f1f5f9',
                                            overflow: 'hidden',
                                            position: 'relative',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => setSelectedImageModal(item)}
                                        title="විශාල කර බැලීමට ක්ලික් කරන්න"
                                    >
                                        <img
                                            src={item.fileData}
                                            alt={item.title}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                transition: 'transform 0.3s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                        />
                                        <div style={{
                                            position: 'absolute',
                                            top: '12px',
                                            right: '12px',
                                            background: 'rgba(0, 0, 0, 0.6)',
                                            color: 'white',
                                            padding: '4px 10px',
                                            borderRadius: '20px',
                                            fontSize: '11px',
                                            backdropFilter: 'blur(4px)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            <span>🔍</span> විශාල කරන්න
                                        </div>
                                    </div>
                                )}

                                {/* Content Section */}
                                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', flex: '1' }}>
                                    {/* Date & Status */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                        <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            📅 {item.date}
                                        </span>
                                        <span style={{
                                            padding: '3px 10px',
                                            borderRadius: '20px',
                                            fontSize: '11.5px',
                                            fontWeight: '700',
                                            background: isActive ? '#ecfdf5' : '#f1f5f9',
                                            color: isActive ? '#065f46' : '#64748b',
                                            border: isActive ? '1px solid #a7f3d0' : '1px solid #cbd5e1'
                                        }}>
                                            {isActive ? '🟢 සක්‍රියයි (Open)' : '🔴 අවසන් (Closed)'}
                                        </span>
                                    </div>

                                    {/* Heading */}
                                    <h3 style={{
                                        margin: '0 0 10px 0',
                                        fontSize: '17px',
                                        fontWeight: '700',
                                        color: '#1e293b',
                                        lineHeight: 1.45
                                    }}>
                                        {item.title}
                                    </h3>

                                    {/* Description */}
                                    {item.description && (
                                        <p style={{
                                            margin: '0 0 18px 0',
                                            fontSize: '13.5px',
                                            color: '#64748b',
                                            lineHeight: 1.55,
                                            flex: '1'
                                        }}>
                                            {item.description}
                                        </p>
                                    )}

                                    {/* Actions */}
                                    <div style={{ marginTop: 'auto', paddingTop: '15px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '10px' }}>
                                        {isPdf ? (
                                            <>
                                                <button
                                                    onClick={() => openPdf(item.fileData, item.fileName)}
                                                    style={{
                                                        flex: '1',
                                                        background: '#0e633d',
                                                        color: '#ffffff',
                                                        border: 'none',
                                                        padding: '10px 14px',
                                                        borderRadius: '8px',
                                                        fontSize: '13.5px',
                                                        fontWeight: '700',
                                                        cursor: 'pointer',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '6px',
                                                        boxShadow: '0 2px 8px rgba(14, 99, 61, 0.2)'
                                                    }}
                                                >
                                                    <span>📄</span> PDF බලන්න
                                                </button>
                                                <button
                                                    onClick={() => downloadFile(item.fileData, item.fileName, 'pdf')}
                                                    style={{
                                                        background: '#f8fafc',
                                                        color: '#334155',
                                                        border: '1px solid #cbd5e1',
                                                        padding: '10px 12px',
                                                        borderRadius: '8px',
                                                        fontSize: '13.5px',
                                                        fontWeight: '600',
                                                        cursor: 'pointer'
                                                    }}
                                                    title="ලේඛනය බාගත කරන්න"
                                                >
                                                    📥
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={() => setSelectedImageModal(item)}
                                                    style={{
                                                        flex: '1',
                                                        background: '#0e633d',
                                                        color: '#ffffff',
                                                        border: 'none',
                                                        padding: '10px 14px',
                                                        borderRadius: '8px',
                                                        fontSize: '13.5px',
                                                        fontWeight: '700',
                                                        cursor: 'pointer',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '6px',
                                                        boxShadow: '0 2px 8px rgba(14, 99, 61, 0.2)'
                                                    }}
                                                >
                                                    <span>🖼️</span> ඡායාරූපය බලන්න
                                                </button>
                                                <button
                                                    onClick={() => downloadFile(item.fileData, item.fileName, 'image')}
                                                    style={{
                                                        background: '#f8fafc',
                                                        color: '#334155',
                                                        border: '1px solid #cbd5e1',
                                                        padding: '10px 12px',
                                                        borderRadius: '8px',
                                                        fontSize: '13.5px',
                                                        fontWeight: '600',
                                                        cursor: 'pointer'
                                                    }}
                                                    title="ඡායාරූපය බාගත කරන්න"
                                                >
                                                    📥
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Modal for Full Image View */}
            {selectedImageModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        backdropFilter: 'blur(5px)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                    }}
                    onClick={() => setSelectedImageModal(null)}
                >
                    <div
                        style={{
                            maxWidth: '900px',
                            maxHeight: '90vh',
                            background: '#ffffff',
                            borderRadius: '14px',
                            overflow: 'hidden',
                            position: 'relative',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{
                            padding: '16px 20px',
                            background: '#0e633d',
                            color: 'white',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                                {selectedImageModal.title}
                            </h3>
                            <button
                                onClick={() => setSelectedImageModal(null)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    lineHeight: 1
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div style={{ padding: '10px', overflowY: 'auto', textAlign: 'center', background: '#0f172a' }}>
                            <img
                                src={selectedImageModal.fileData}
                                alt={selectedImageModal.title}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '75vh',
                                    objectFit: 'contain',
                                    borderRadius: '6px'
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
